/**
 * Keeps the matter true. Everything that says "something happened" — a watch
 * event, an answer to our own call, a `hello` after a reconnect, a polled head
 * — is only a nudge: bits change only by a `planes` snapshot or by journal
 * commits applied in chain order. Commits ride on `commit` events; when one
 * is missing, truncated or out of chain, the journal is read from our seq,
 * once per World at a time, and a World too far behind (or whose chain broke)
 * is read whole again and its difference played as a correction. A read the
 * World answered before commits we have since shown is carried up to them
 * through the journal first, so no commit ever plays twice.
 *
 * Every seal carries who is thought to have caused it, by the commit's hash:
 * our own answers name their head, and a peer's settled "act" frame names its
 * own. A seal one of our calls still at the door could have made waits for
 * that call's verdict (at most 300 ms, the pacing rule of §4.4), so the loser
 * of a race never sees the winner's seal in its own colour. Records
 * (occupiers, relays, asks…) are refetched by the table in DESIGN §5.5, never
 * for bits, for every World a socket of ours hears, tracked or not (the
 * Inbox reads asks of a World nobody tracks). A hello that says the World
 * moved while nobody heard (its seq, its counts of occupiers and relays, its
 * seats) refetches the records already read of it: a declare or a seat
 * leaves no commit, so the journal alone cannot bring them back.
 *
 * Each tracked World listens on the socket the pool holds for it (a persona's
 * World on the one opened with that persona's key, `B:mill`), and follows it
 * when the pool closes or replaces it; a World with no socket here (one whose
 * socket was closed) is polled for its head every 5 s instead.
 */

import type {
  Attribution, CallRecord, CreateReconciler, Flip, JournalCommit, Json, Link, Nudge, PlanesAnswer, PresenceOut,
  Reconciler, RecordKind, Source, Unsub, Window, WorldDescribe, WorldEvent,
} from '../contracts.ts';
import { answerOf, createPlaneModel } from './planes.ts';
import { occupiersOf, worldKey, worldRef } from './records.ts';

type By = Attribution['source'];
type Timers = { set(fn: () => void, ms: number): unknown; clear(id: unknown): void };
type Deps = Parameters<CreateReconciler>[0];

/** Commits are read this many at a time; past `TOO_FAR` behind, a fresh snapshot is cheaper. */
export const JOURNAL_PAGE = 1000;
export const TOO_FAR = 5000;
export const COALESCE_MS = 60;
export const WINDOW_DEBOUNCE_MS = 150;
export const ANSWER_GRACE_MS = 250;
/** A seal one of our calls could have made waits this long for that call's verdict. */
export const VERDICT_WAIT_MS = 300;
/** A World with no socket here is asked for its head this often (§7 rule 3). */
export const POLL_MS = 5000;
const DEDUPE = 512;
const PEER_ACT_MS = 3000;
/** Recent event times kept to read the World's clock by. */
const OFFSETS = 15;

/** Records an act changes, by the `commit` event's `data.op` or the event's type (§5.5 rule 10). */
export const REFETCH: Readonly<Record<string, readonly RecordKind[]>> = {
  declare: ['occupiers', 'describe'],
  capacity: ['occupiers', 'describe'],
  takeIn: ['hands', 'occupiers'],
  relayLay: ['relays'],
  relayAct: ['relays'],
  'relay.acted': ['relays'],
  'ask.received': ['asks'],
  'ask.withdrawn': ['asks'],
  'ask.answered': ['asks'],
  receive: ['asks'],
  withdrawAsk: ['asks'],
  answer: ['asks'],
};

/** Record kinds that belong to one World (the rest are the account's). */
const WORLD_KINDS: ReadonlySet<RecordKind> = new Set(['describe', 'occupiers', 'hands', 'relays', 'asks']);
/** What a hello brings back when the World moved unheard: every World record but describe, which the hello itself carries. */
const STALE_KINDS: readonly RecordKind[] = ['occupiers', 'hands', 'relays', 'asks'];

/** The same, by the operation we called, for when no event comes (the socket is down). */
const REFETCH_BY_OP: Readonly<Record<string, readonly RecordKind[]>> = {
  declareOccupier: ['occupiers', 'describe'],
  setCapacity: ['occupiers', 'describe'],
  takeIn: ['hands', 'occupiers'],
  layRelay: ['relays'],
  relayAct: ['relays'],
  answerAsk: ['asks'],
};

/** Our own account calls: what each changes. */
const ACCOUNT: Readonly<Record<string, readonly RecordKind[]>> = {
  createKey: ['keys'], revokeKey: ['keys'], rotateKey: ['keys'],
  giveGrant: ['grants'], revokeGrant: ['grants'],
  createWebhook: ['webhooks'], deleteWebhook: ['webhooks'],
  checkout: ['billing'], portal: ['billing'], mockBillingAct: ['billing', 'usage'],
  createWorld: ['usage'],
};

/** Account resources another tab may have changed first: last writer wins, so a 404 or 409 re-lists them (§7 rule 13). */
const CONTESTED: ReadonlySet<RecordKind> = new Set(['keys', 'grants', 'webhooks']);

/** Operations that can seal commits, and the commit kinds each seals: to match a seal to a call at the door. */
const SEALS: Readonly<Record<string, readonly string[]>> = {
  claim: ['claim', 'displace', 'hold'],
  release: ['release'],
  handover: ['handover'],
  confirm: ['confirm'],
  giveUp: ['release'],
  queue: ['hold'],
  sweep: ['lapse'],
  takeIn: ['takeIn', 'reconcile'],
  relayAct: ['propose', 'withdraw', 'accept', 'reject', 'release', 'move'],
  answerAsk: ['accept', 'reject', 'release', 'move'],
};

/** The occupier a door call acts as, which the commit names as its actor. */
const actorArg = (record: CallRecord): string | null => {
  const body = record.args.body;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const who = record.op === 'handover' ? body.to : body.who;
  return typeof who === 'string' ? who : null;
};

const isOk = (status: number | undefined): boolean => status !== undefined && status >= 200 && status < 300;

interface AtDoor { readonly id: string; readonly op: string; readonly actor: string | null; readonly source: Source; readonly at: number; used: boolean }
interface PeerAct { readonly sid: string; readonly call: string; readonly op: string; readonly at: number; used: boolean }

/** A seal waiting to be told: its commit, the act it belongs to (the act's head), and our calls that could have made it. */
interface Pending { readonly c: JournalCommit; readonly act: string; readonly flips: readonly Flip[]; readonly ours: readonly AtDoor[]; readonly since: number }

interface Tracked {
  window: Window;
  link: Link | undefined;
  linkUnsubs: Unsub[];
  /** Polled for its head: no socket of ours listens. */
  polling: boolean;
  pollTimer: unknown;
  snapping: Promise<void> | null;
  snapAgain: boolean;
  heardWhileSnapping: boolean;
  fetching: Promise<void> | null;
  fetchAgain: boolean;
  fetchTimer: unknown;
  windowTimer: unknown;
  waiting: Array<() => void>;
  /** From a hello: the journal must reach this head. */
  needHead: string | null;
  /** Hashes applied lately, so a late answer knows its commit already landed. */
  applied: string[];
}

/** The reconciler; `serverNow` is local time until an event has been heard. */
export const createReconciler: CreateReconciler = (deps: Deps): Reconciler => {
  const { client, planes, records, links } = deps;
  const now = deps.now ?? Date.now;
  const timers: Timers = deps.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>) };

  const tracked = new Map<string, Tracked>();
  const flipListeners = new Set<(flips: readonly Flip[], by: Attribution) => void>();
  const recordListeners = new Set<(world: string, kinds: readonly RecordKind[]) => void>();
  /** head → who sealed it, from answers (ours) and settled act frames (peers'). */
  const heads = new Map<string, By>();
  const seen = new Map<string, true>();
  const atDoor = new Map<string, AtDoor[]>();
  const peerActs = new Map<string, PeerAct[]>();
  const answered = new Map<string, true>();
  const graceTimers = new Map<string, unknown>();
  /** Seals not yet told, per World, in chain order. */
  const pending = new Map<string, Pending[]>();
  /** What the last hello (or polled head) said of each World: seq, head, occupiers, relays, seats. */
  const said = new Map<string, string>();
  const pendingTimers = new Map<string, unknown>();
  const offsets: number[] = [];

  const remember = <K>(map: Map<K, unknown>, key: K, value: unknown, most = DEDUPE): void => {
    map.delete(key);
    map.set(key, value);
    while (map.size > most) map.delete(map.keys().next().value as K);
  };

  const emitFlips = (flips: readonly Flip[], by: Attribution): void => {
    if (flips.length === 0) return;
    for (const fn of [...flipListeners]) {
      try { fn(flips, by); } catch { /* a listener's fault is its own */ }
    }
  };

  /** Refetch what an act changed (only kinds someone has read), and say so. `world` is '' for the account. */
  const refetch = (world: string, kinds: readonly RecordKind[]): void => {
    if (kinds.length === 0) return;
    const unique = [...new Set(kinds)];
    for (const kind of unique) {
      const at = WORLD_KINDS.has(kind) ? world : null;
      if (at === '') continue;
      if (records.get(at, kind) !== undefined) void records.refresh(at, kind);
    }
    for (const fn of [...recordListeners]) {
      try { fn(world, unique); } catch { /* a listener's fault is its own */ }
    }
  };

  /** A World's hello, heard for its records: when it says the World moved unheard, what was read of it is read again. */
  const hearRecords = (world: string, d: WorldDescribe): void => {
    const saying = `${d.seq}|${d.head}|${d.occupiers}|${d.relays}|${d.seats}`;
    const before = said.get(world);
    remember(said, world, saying, DEDUPE);
    if (before === saying) return;
    const listed = (kind: RecordKind, field: 'occupiers' | 'relays'): number | null => {
      const v = records.get(world, kind);
      const list = v && typeof v === 'object' && !Array.isArray(v) ? v[field] : null;
      return Array.isArray(list) ? list.length : null;
    };
    const described = records.get(world, 'describe');
    const d0 = described && typeof described === 'object' && !Array.isArray(described) ? described : null;
    const stale = before !== undefined
      // The first hello we hear: stale if what we hold disagrees with it.
      || (listed('occupiers', 'occupiers') ?? d.occupiers) !== d.occupiers
      || (listed('relays', 'relays') ?? d.relays) !== d.relays
      || (d0 !== null && (d0.seq !== d.seq || d0.seats !== d.seats || d0.occupiers !== d.occupiers || d0.relays !== d.relays))
      || (planes.get(world)?.seq ?? d.seq) < d.seq;
    if (!stale) return;
    if (d0 !== null) records.put(world, 'describe', { ...d0, ...d } as unknown as Json);
    refetch(world, STALE_KINDS);
  };

  // -- attribution ---------------------------------------------------------------------

  /** Our calls at the door that could have sealed `c`. */
  const oursFor = (world: string, c: JournalCommit): AtDoor[] => {
    const kinds = (op: string) => SEALS[op] ?? [];
    return (atDoor.get(world) ?? []).filter((d) =>
      kinds(d.op).includes(c.kind) && (d.actor === null || c.actor === null || d.actor === c.actor));
  };

  /**
   * Who sealed a commit: whoever's answer (ours) or settled frame (a peer's)
   * named its head or its act's head. Failing that, once every call of ours
   * that could have made it has its verdict, a peer that said it was acting,
   * else nobody we know. If the wait ran out with ours still at the door, the
   * first to send, ours or the peer's, is the guess. Each call stands for one
   * seal at most.
   */
  const attribute = (world: string, p: Pending): By => {
    const known = heads.get(p.c.hash) ?? heads.get(p.act);
    if (known) return known;
    const fits = (op: string) => (SEALS[op] ?? []).includes(p.c.kind);
    const peer = (peerActs.get(world) ?? []).find((a) => !a.used && now() - a.at <= PEER_ACT_MS && fits(a.op));
    const mine = p.ours.find((d) => !d.used && !answered.has(d.id));
    if (mine && (!peer || mine.at <= peer.at)) {
      mine.used = true;
      return mine.source;
    }
    if (peer) {
      peer.used = true;
      return { kind: 'peer', sid: peer.sid, unverified: true };
    }
    return { kind: 'unknown' };
  };

  /** Is a seal's maker known, or has every call of ours that could be it been answered? */
  const settled = (p: Pending): boolean =>
    heads.has(p.c.hash) || heads.has(p.act) || p.ours.every((d) => answered.has(d.id));

  /**
   * Tell the seals at the front of a World's line whose maker is settled.
   * `due`: the first has waited its time and goes whatever. `all`: everything
   * goes now (a snapshot is about to play, or the World is let go).
   */
  const release = (world: string, how: 'ready' | 'due' | 'all' = 'ready'): void => {
    const timer = pendingTimers.get(world);
    if (timer !== undefined) { timers.clear(timer); pendingTimers.delete(world); }
    const line = pending.get(world);
    if (!line) return;
    let force = how !== 'ready';
    while (line.length > 0 && (force || settled(line[0]!))) {
      if (how === 'due') force = false;
      const p = line.shift()!;
      emitFlips(p.flips, { head: p.c.hash, source: attribute(world, p) });
    }
    if (line.length === 0) { pending.delete(world); return; }
    const wait = Math.max(0, VERDICT_WAIT_MS - (now() - line[0]!.since));
    pendingTimers.set(world, timers.set(() => {
      pendingTimers.delete(world);
      release(world, 'due');
    }, wait));
  };

  /**
   * Flips of several commits, told one commit at a time so each seal carries
   * its own head. `act`: the head of the act that sealed them all (a commit
   * event carries one act's commits).
   */
  const emitByCommit = (world: string, commits: readonly JournalCommit[], flips: readonly Flip[], act: string | null = null): void => {
    const t = tracked.get(world);
    const line = pending.get(world) ?? [];
    for (const c of commits) {
      if (t) { t.applied.push(c.hash); if (t.applied.length > 256) t.applied.shift(); }
      const mine = flips.filter((f) => f.cause.kind === 'commit' && f.cause.hash === c.hash);
      // Nothing in view changed: nothing to tell, and nobody to credit.
      if (mine.length === 0) continue;
      const actHead = act ?? c.hash;
      const known = heads.has(c.hash) || heads.has(actHead);
      line.push({ c, act: actHead, flips: mine, ours: known ? [] : oursFor(world, c), since: now() });
    }
    if (line.length > 0) pending.set(world, line);
    release(world);
  };

  // -- snapshots and the journal ------------------------------------------------------------

  const snapshot = (world: string): Promise<void> => {
    const t = tracked.get(world);
    if (!t) return Promise.resolve();
    if (t.snapping) { t.snapAgain = true; return t.snapping; }
    const run = (async () => {
      try {
        do {
          t.snapAgain = false;
          t.heardWhileSnapping = false;
          await snapshotOnce(world, t);
        } while (t.snapAgain && tracked.get(world) === t);
      } finally {
        t.snapping = null;
      }
      // Commits heard while the read was out may be older or newer than it: the journal settles which.
      if (t.heardWhileSnapping && tracked.get(world) === t) scheduleFetch(world);
    })();
    t.snapping = run;
    return run;
  };

  /**
   * A window read the World answered before commits we have since applied and
   * shown, carried up to exactly what is shown through the journal, off to the
   * side: those commits then never play twice, nor flicker back as a
   * correction in between. Null when the journal does not lead from the read
   * to what is shown (or cannot be read): the read then stands as the truth.
   */
  const bringUp = async (world: string, t: Tracked, answer: PlanesAnswer, window: Window): Promise<PlanesAnswer | null> => {
    const { persona, w } = worldRef(world);
    const side = createPlaneModel();
    side.snapshot(world, answer, window);
    const read = side.get(world)!;
    const shownKinds = planes.get(world)?.kinds;
    // Kinds the record named since outlive the read, which knows only its own planes'.
    if (shownKinds) for (const [id, kind] of shownKinds) if (!read.kinds.has(id)) read.kinds.set(id, kind);
    for (;;) {
      const at = side.get(world)!;
      const shown = planes.get(world);
      if (!shown || shown.seq - at.seq > TOO_FAR) return null;
      if (at.seq >= shown.seq) return at.seq === shown.seq && at.head === shown.head ? answerOf(at) : null;
      const record = await client.call('journal', { w, since: at.seq, limit: Math.min(JOURNAL_PAGE, shown.seq - at.seq) }, { persona, source: { kind: 'auto' } });
      if (tracked.get(world) !== t) return null;
      const reply = record.answer;
      if (!reply || reply.status !== 200) return null;
      const page = (reply.body as unknown as { commits?: JournalCommit[] }).commits ?? [];
      // What is shown may have moved on while the page was out: carry the read no further than that.
      const upTo = planes.get(world)?.seq ?? shown.seq;
      const some = page.filter((c) => c.seq <= upTo);
      if (some.length === 0) return null;
      const result = side.apply(world, some);
      if ('gap' in result || 'broken' in result) return null;
    }
  };

  /** Positions one /planes read may span: a month, and the one more a local month in any zone touches. */
  const MAX_WINDOW = 32;

  const snapshotOnce = async (world: string, t: Tracked): Promise<void> => {
    const { persona, w } = worldRef(world);
    const window = t.window;
    const args: Record<string, Json> = { w, date: window.from.date, positions: Math.max(1, Math.min(MAX_WINDOW, window.positions)) };
    if (window.from.hour !== undefined) args.hour = window.from.hour;
    const record = await client.call('planes', args, { persona, source: { kind: 'auto' } });
    const answer = record.answer;
    if (!answer || answer.status !== 200 || tracked.get(world) !== t) return;
    let body = answer.body as unknown as PlanesAnswer;
    const shown = planes.get(world);
    let broughtUp = false;
    if (shown && body.seq < shown.seq) {
      let brought: PlanesAnswer | null = body;
      // What is shown may move on again before this resumes: carry the read until it has caught up.
      while (brought && brought.seq < (planes.get(world)?.seq ?? brought.seq)) {
        brought = await bringUp(world, t, brought, window);
        if (tracked.get(world) !== t) return;
      }
      if (brought) { body = brought; broughtUp = true; }
    }
    // Seals still waiting for a verdict are older than this read: they go first.
    release(world, 'all');
    const flips = planes.snapshot(world, body, window);
    // A read carried up to what was shown says nothing about a hello's head; a fresh one does.
    if (!broughtUp) t.needHead = null;
    const occupiers = records.get(world, 'occupiers');
    if (occupiers !== undefined) planes.learnKinds(world, occupiersOf(occupiers));
    emitFlips(flips, { head: body.head, source: { kind: 'auto' } });
  };

  const scheduleFetch = (world: string): void => {
    const t = tracked.get(world);
    if (!t || t.fetchTimer !== null) return;
    t.fetchTimer = timers.set(() => {
      t.fetchTimer = null;
      void fetchJournal(world);
    }, COALESCE_MS);
  };

  /** One journal read at a time per World; one asked for meanwhile runs once more after it. */
  const fetchJournal = (world: string): Promise<void> => {
    const t = tracked.get(world);
    if (!t) return Promise.resolve();
    if (t.fetching) { t.fetchAgain = true; return t.fetching; }
    const run = (async () => {
      try {
        do {
          t.fetchAgain = false;
          await catchUp(world, t);
        } while (t.fetchAgain && tracked.get(world) === t);
      } finally {
        t.fetching = null;
      }
    })();
    t.fetching = run;
    return run;
  };

  const catchUp = async (world: string, t: Tracked): Promise<void> => {
    if (t.snapping) await t.snapping;
    if (!planes.get(world)) return snapshot(world);
    const { persona, w } = worldRef(world);
    // The hello head this pass may judge: one heard before its reads went out. One heard meanwhile is
    // newer than they are, and the pass queued behind this one reads up to it.
    const need = t.needHead;
    for (;;) {
      const m = planes.get(world);
      if (!m) return;
      const record = await client.call('journal', { w, since: m.seq, limit: JOURNAL_PAGE }, { persona, source: { kind: 'auto' } });
      const answer = record.answer;
      if (!answer || answer.status !== 200 || tracked.get(world) !== t) return;
      const body = answer.body as unknown as { head: string; seq: number; commits: JournalCommit[] };
      if (body.seq - m.seq > TOO_FAR) return snapshot(world);
      if (body.commits.length === 0) {
        // Nothing after our seq, yet another head: our chain is not the World's.
        if (body.seq === m.seq && body.head !== m.head) return snapshot(world);
        break;
      }
      const before = planes.get(world)?.seq ?? m.seq;
      const result = planes.apply(world, body.commits);
      if ('gap' in result || 'broken' in result) return snapshot(world);
      emitByCommit(world, body.commits.filter((c) => c.seq > before), result.flips);
      if (result.seq >= body.seq || body.commits.length < JOURNAL_PAGE) break;
    }
    const m = planes.get(world);
    if (need !== null && m) {
      if (t.needHead === need) t.needHead = null;
      // The hello's head must be on our chain by now; if it is not, read the World whole.
      if (m.head !== need && !t.applied.includes(need)) return snapshot(world);
    }
  };

  // -- nudges ---------------------------------------------------------------------------------

  const onEvent = (world: string, e: WorldEvent): void => {
    const t = tracked.get(world);
    const key = `${e.id}|${e.head}`;
    // Never by commitSeq: a confirm reports a stale seq, so its id collides with the commit before it.
    if (seen.has(key)) return;
    remember(seen, key, true);
    if (Number.isFinite(e.at) && e.at > 0) {
      offsets.push(e.at - now());
      if (offsets.length > OFFSETS) offsets.shift();
    }
    // Records first, tracked or not: a screen that reads a World's asks or roster without its bits (the Inbox) hears them too.
    const op = typeof e.data.op === 'string' ? e.data.op : null;
    refetch(world, [...(op ? REFETCH[op] ?? [] : []), ...(e.type !== 'commit' ? REFETCH[e.type] ?? [] : [])]);
    if (!t) return;
    if (t.snapping) t.heardWhileSnapping = true;

    const m = planes.get(world);
    // No matter yet: the first read is either out (and will catch this up after) or needed now.
    if (!m) { if (!t.snapping) void snapshot(world); return; }
    if (e.type === 'commit') {
      const commits = e.data.commits;
      if (Array.isArray(commits) && commits.length > 0) {
        const list = (commits as unknown as JournalCommit[]).slice().sort((a, b) => a.seq - b.seq);
        const before = m.seq;
        const result = planes.apply(world, list);
        if ('gap' in result || 'broken' in result) scheduleFetch(world);
        // One event carries one act's commits, cut at the act's head.
        else emitByCommit(world, list.filter((c) => c.seq > before), result.flips, e.head);
        return;
      }
    }
    // Truncated, carried nothing, or a typed event: behind only if its head is not ours.
    if (e.head !== null && e.head !== planes.get(world)?.head && !t.applied.includes(e.head)) scheduleFetch(world);
  };

  const onAnswer = (record: CallRecord): void => {
    if (answered.has(record.id)) return;
    const answer = record.answer;
    if (!answer) return;
    remember(answered, record.id, true);
    const w = record.args.w;
    const world = typeof w === 'string' ? worldKey(record.persona, w) : null;
    if (world) {
      const waiting = atDoor.get(world);
      if (waiting) atDoor.set(world, waiting.filter((d) => d.id !== record.id));
    }
    // Only a write's head is its own; a read's head is whatever the World last sealed.
    if (answer.head && SEALS[record.op] && isOk(answer.status)) remember(heads, answer.head, record.source);
    // A seal that waited for this verdict can be told now, ours or not.
    if (world) release(world);

    const account = ACCOUNT[record.op];
    if (account && record.persona === 'A') {
      // Someone else got there first (last writer wins): what we hold of it is stale.
      const lost = (answer.status === 404 || answer.status === 409) && account.some((k) => CONTESTED.has(k));
      if (isOk(answer.status) || lost) refetch(world ?? '', account);
    }
    if (!isOk(answer.status)) return;
    if (!world) return;
    const t = tracked.get(world);
    if (!t) return;
    // A read's head is only when it was read (maybe before our latest seal): writes alone say we are behind.
    const head = SEALS[record.op] ? answer.head : null;
    const kinds = REFETCH_BY_OP[record.op] ?? [];
    if (head === null && kinds.length === 0) return;
    const key = `${world}|${head ?? record.id}`;
    if (graceTimers.has(key)) return;
    // Events come before answers; if none has brought this head within the grace, the socket is slow.
    graceTimers.set(key, timers.set(() => {
      graceTimers.delete(key);
      if (tracked.get(world) !== t) return;
      const live = t.link?.state() === 'live';
      if (!live) refetch(world, kinds);
      const m = planes.get(world);
      if (head !== null && m && m.head !== head && !t.applied.includes(head)) scheduleFetch(world);
    }, ANSWER_GRACE_MS));
  };

  const onSent = (record: CallRecord): void => {
    if (!SEALS[record.op]) return;
    const w = record.args.w;
    if (typeof w !== 'string') return;
    const world = worldKey(record.persona, w);
    const list = atDoor.get(world) ?? [];
    list.push({ id: record.id, op: record.op, actor: actorArg(record), source: record.source, at: now(), used: false });
    atDoor.set(world, list.slice(-32));
  };

  const onPresence = (world: string, f: PresenceOut): void => {
    if (f.type !== 'presence' || f.frame.t !== 'act') return;
    const frame = f.frame;
    const live = (peerActs.get(world) ?? []).filter((p) => now() - p.at <= PEER_ACT_MS);
    if (frame.ph === 'sent') {
      live.push({ sid: f.sid, call: frame.call, op: frame.op, at: now(), used: false });
      peerActs.set(world, live.slice(-16));
      return;
    }
    // Settled: the act no longer stands in for a seal. A refused one made none; one that sealed names its head.
    peerActs.set(world, live.filter((p) => !(p.sid === f.sid && p.call === frame.call)));
    const sealed = frame.status === undefined || isOk(frame.status);
    if (sealed && frame.head && !heads.has(frame.head)) {
      remember(heads, frame.head, { kind: 'peer', sid: f.sid, unverified: true });
      release(world);
    }
  };

  const onHello = (world: string, seq: number, head: string): void => {
    const t = tracked.get(world);
    if (!t) return;
    const m = planes.get(world);
    if (!m) { if (t.snapping) t.heardWhileSnapping = true; else void snapshot(world); return; }
    if (seq > m.seq) { t.needHead = head; scheduleFetch(world); return; }
    if (seq === m.seq && head === m.head) return;
    // Same length, other head, or shorter than ours: not our chain.
    void snapshot(world);
  };

  // -- links and polling ----------------------------------------------------------------------

  const stopPoll = (t: Tracked): void => {
    if (t.pollTimer !== null) timers.clear(t.pollTimer);
    t.pollTimer = null;
    t.polling = false;
  };

  /** A World with no socket here: ask for its head now and then, and read the journal when it moved. */
  const poll = (world: string, t: Tracked): void => {
    t.polling = true;
    if (t.pollTimer !== null) return;
    t.pollTimer = timers.set(() => {
      t.pollTimer = null;
      if (tracked.get(world) !== t || !t.polling) return;
      const { persona, w } = worldRef(world);
      void client.call('getWorld', { w }, { persona, source: { kind: 'auto' } }).then((record) => {
        if (tracked.get(world) !== t || !t.polling) return;
        const body = record.answer?.status === 200 ? record.answer.body : null;
        const head = body && typeof body === 'object' && !Array.isArray(body) ? body.head : null;
        const m = planes.get(world);
        if (typeof head === 'string' && m && m.head !== head && !t.applied.includes(head)) scheduleFetch(world);
        poll(world, t);
      });
    }, POLL_MS);
  };

  const unbind = (t: Tracked): void => {
    for (const u of t.linkUnsubs.splice(0)) u();
    t.link = undefined;
  };

  /**
   * Listen on the socket the pool holds for this World now; with `open`, open
   * one if there is none (under the World key, so a persona's World watches
   * with that persona's key). A socket that was replaced or closed is let go;
   * whatever it missed costs one journal read. With none, the World is polled.
   */
  const bind = (world: string, t: Tracked, open: boolean): void => {
    const inPool = links.get(world);
    const current = inPool && inPool.state() !== 'closed' ? inPool : undefined;
    if (t.link && t.link === current) return;
    // Bound before to a socket now gone, or polled meanwhile: what no socket of ours heard costs one journal read.
    const missed = t.link !== undefined || t.polling;
    unbind(t);
    const link = current ?? (open ? links.open(world, { presence: true }) : undefined);
    if (!link) {
      poll(world, t);
      return;
    }
    stopPoll(t);
    t.link = link;
    t.linkUnsubs.push(
      link.onEvent((e) => onEvent(world, e)),
      link.onHello((d) => {
        if (d.globalName) planes.name(world, d.globalName);
        // Against what was held before this hello is written over it.
        hearRecords(world, d);
        const before = records.get(world, 'describe');
        const merged = before && typeof before === 'object' && !Array.isArray(before) ? { ...before, ...d } : { ...d };
        records.put(world, 'describe', merged as unknown as Json);
        onHello(world, d.seq, d.head);
      }),
      link.onPresence((f) => onPresence(world, f)),
      link.onState((s) => {
        if (s !== 'closed' || tracked.get(world) !== t || t.link !== link) return;
        // The pool closed it (evicted, or every link closed): keep up by polling until a socket is back.
        unbind(t);
        poll(world, t);
      }),
    );
    if (missed && planes.get(world)) scheduleFetch(world);
  };

  client.on((record, phase) => {
    if (phase === 'sent') onSent(record);
    else onAnswer(record);
  });

  records.on((world, kind) => {
    if (world === null || !tracked.has(world)) return;
    if (kind === 'occupiers') planes.learnKinds(world, occupiersOf(records.get(world, 'occupiers')));
    if (kind === 'describe') {
      const d = records.get(world, 'describe');
      const seats = d && typeof d === 'object' && !Array.isArray(d) ? d.seats : undefined;
      if (typeof seats === 'number') planes.learnSeats(world, seats);
    }
  });

  const sameWindow = (a: Window, b: Window): boolean =>
    a.from.date === b.from.date && a.from.hour === b.from.hour && a.positions === b.positions;

  const reconciler: Reconciler = {
    track(world, window) {
      const existing = tracked.get(world);
      if (existing) {
        bind(world, existing, true);
        if (sameWindow(existing.window, window) && planes.get(world)) return Promise.resolve();
        existing.window = window;
        if (existing.windowTimer !== null) timers.clear(existing.windowTimer);
        return new Promise<void>((resolve) => {
          existing.waiting.push(resolve);
          existing.windowTimer = timers.set(() => {
            existing.windowTimer = null;
            const waiting = existing.waiting.splice(0);
            void snapshot(world).finally(() => waiting.forEach((r) => r()));
          }, WINDOW_DEBOUNCE_MS);
        });
      }
      const t: Tracked = {
        window, link: undefined, linkUnsubs: [], polling: false, pollTimer: null, snapping: null, snapAgain: false, heardWhileSnapping: false,
        fetching: null, fetchAgain: false, fetchTimer: null, windowTimer: null, waiting: [], needHead: null, applied: [],
      };
      tracked.set(world, t);
      bind(world, t, true);
      return snapshot(world);
    },

    // The socket stays with the pool, which keeps recently viewed Worlds open (§5.6) and closes the oldest.
    untrack(world) {
      const t = tracked.get(world);
      if (!t) return;
      tracked.delete(world);
      unbind(t);
      stopPoll(t);
      if (t.fetchTimer !== null) timers.clear(t.fetchTimer);
      if (t.windowTimer !== null) timers.clear(t.windowTimer);
      t.waiting.splice(0).forEach((r) => r());
      const timer = pendingTimers.get(world);
      if (timer !== undefined) timers.clear(timer);
      pendingTimers.delete(world);
      pending.delete(world);
      planes.forget(world);
    },

    nudge(world, n: Nudge) {
      const t = tracked.get(world);
      // A socket the pool opened or replaced since: follow it (never open one from a nudge).
      if (t) bind(world, t, false);
      switch (n.kind) {
        case 'event': return onEvent(world, n.event);
        case 'answer': return onAnswer(n.record);
        case 'hello':
          if (n.describe) hearRecords(world, n.describe);
          return onHello(world, n.seq, n.head);
        case 'poll': {
          const m = planes.get(world);
          if (t && (!m || m.head !== n.head) && !t.applied.includes(n.head)) scheduleFetch(world);
          return;
        }
        case 'window': { void reconciler.track(world, n.window); return; }
      }
    },

    onFlips(fn) {
      flipListeners.add(fn);
      return () => { flipListeners.delete(fn); };
    },

    onRecords(fn) {
      recordListeners.add(fn);
      return () => { recordListeners.delete(fn); };
    },

    serverNow() {
      if (offsets.length === 0) return now();
      const sorted = [...offsets].sort((a, b) => a - b);
      const mid = sorted.length >> 1;
      const median = sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
      return Math.round(now() + median);
    },
  };
  return reconciler;
};

